import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getCurrentProject,
  getCurrentProjectFetchState,
} from '@neptune/current-project-business-logic';
import { fetchWorkspaceStatusAction } from '@neptune/pricing-workspace-status-api';
import { ProjectView } from '@neptune/project-ui';
import { useNoAccess, useNotFound } from '@neptune/shared/common-feature';
import { isArchivingEnabled } from '@neptune/shared/core-organizations-business-logic';

import LoadingIndicator from 'components/loading-indicator/LoadingIndicator';
import { CurrentProjectProvider } from 'components/providers/CurrentProjectProvider';
import { getContextOrganization, getOrganizationNameFromRouteParams } from 'state/selectors-global';

import { ArchivedProjectContainer } from './ArchivedProjectContainer';
import { ProjectSideEffectsContainer } from './ProjectSideEffectsContainer';

export const ProjectViewContainer: React.FC<{
  header: React.ReactNode;
  scene: React.ReactNode;
}> = ({ header, scene }) => {
  const dispatch = useDispatch();

  const project = useSelector(getCurrentProject);
  const { fetchStatus, error } = useSelector(getCurrentProjectFetchState);

  const HttpErrorPage = useNotFound({ error, fetchStatus });
  useNoAccess({ error, fetchStatus });

  const organizationIdentifier = useSelector(getOrganizationNameFromRouteParams);

  React.useEffect(() => {
    dispatch(fetchWorkspaceStatusAction({ organizationIdentifier }));
  }, [dispatch, organizationIdentifier]);

  const workspace = useSelector(getContextOrganization);
  const archivingEnabled = workspace ? isArchivingEnabled(workspace.traits) : false;

  return (
    <CurrentProjectProvider>
      {() => {
        if (HttpErrorPage) {
          return <HttpErrorPage />;
        }

        if (!project) {
          return <LoadingIndicator centered isVisible />;
        }

        if (archivingEnabled && project?.archived) {
          return <ArchivedProjectContainer />;
        }

        return (
          <>
            <ProjectView header={header} scene={scene} />
            <ProjectSideEffectsContainer />
          </>
        );
      }}
    </CurrentProjectProvider>
  );
};
